import { useEffect, useState } from "react";
import { api, ROLE_LABELS, formatDateTime } from "@/lib/api";
import { Vote, Plus, CheckCircle2, Lock, Clock } from "lucide-react";
import { toast } from "sonner";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const newOptions = () => [{ id: uid(), value: "" }, { id: uid(), value: "" }];

export default function Voting({ isAdmin }) {
  const [polls, setPolls] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", options: newOptions(), audience_role: "tutti", closes_at: "" });

  const load = () => api.get("/polls").then(r => setPolls(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const options = form.options.map(o => o.value).filter(v => v.trim());
    if (!form.title || options.length < 2) { toast.error("Titolo e almeno 2 opzioni"); return; }
    try {
      await api.post("/polls", { ...form, options });
      setForm({ title: "", description: "", options: newOptions(), audience_role: "tutti", closes_at: "" });
      setShowForm(false); toast.success("Votazione creata"); load();
    } catch { toast.error("Errore creazione"); }
  };

  const vote = async (pollId, optId) => {
    try {
      await api.post(`/polls/${pollId}/vote`, { option_id: optId });
      toast.success("Voto registrato"); load();
      window.dispatchEvent(new CustomEvent("giacomo:say", { detail: { text: "Bravo, il tuo voto conta! 🗳️" } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore voto");
    }
  };

  const closePoll = async (id) => {
    await api.post(`/polls/${id}/close`); toast.success("Votazione chiusa"); load();
  };

  return (
    <div data-testid="section-voting">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-2"><Vote className="w-3 h-3" /> Governance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Votazioni a Distanza</h1>
          <p className="text-[#6B6558] mt-2">I soci esprimono il proprio voto ovunque si trovino. Un socio, un voto.</p>
        </div>
        {isAdmin && (
          <button data-testid="new-poll-btn" onClick={() => setShowForm(v => !v)} className="terracotta-btn px-4 py-2.5 rounded-xl inline-flex items-center gap-2 font-semibold">
            <Plus className="w-4 h-4" /> Nuova Votazione
          </button>
        )}
      </div>

      {isAdmin && showForm && (
        <form onSubmit={submit} className="paper p-6 rounded-2xl mb-6 space-y-3" data-testid="poll-form">
          <input required data-testid="poll-title" placeholder="Titolo votazione" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          <textarea data-testid="poll-desc" placeholder="Descrizione (opzionale)" rows={2} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          {form.options.map((o, i) => (
            <input key={o.id} data-testid={`poll-opt-${i}`} placeholder={`Opzione ${i+1}`} value={o.value} onChange={e => {
              const val = e.target.value;
              setForm(f => ({...f, options: f.options.map(x => x.id === o.id ? {...x, value: val} : x)}));
            }} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          ))}
          <button type="button" data-testid="add-opt" onClick={() => setForm(f => ({...f, options: [...f.options, {id: uid(), value: ""}]}))} className="text-sm text-[#EF3E36] font-semibold">+ Aggiungi opzione</button>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select data-testid="poll-audience" value={form.audience_role} onChange={e => setForm(f => ({...f, audience_role: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8] bg-white">
              {Object.entries(ROLE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input data-testid="poll-close-date" type="datetime-local" value={form.closes_at} onChange={e => setForm(f => ({...f, closes_at: e.target.value}))} className="w-full px-4 py-2.5 rounded-lg border border-[#E8E2D8]" />
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="poll-submit" className="terracotta-btn px-5 py-2.5 rounded-xl font-semibold">Crea Votazione</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-xl border border-[#E8E2D8]">Annulla</button>
          </div>
        </form>
      )}

      <div className="grid gap-4">
        {polls.length === 0 && (
          <div className="paper p-8 rounded-2xl text-center text-[#6B6558]">Nessuna votazione al momento.</div>
        )}
        {polls.map(p => {
          const total = p.total_votes || 0;
          return (
            <div key={p.id} data-testid={`poll-${p.id}`} className="paper p-6 rounded-2xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-2xl font-bold text-[#2B3A33]">{p.title}</h3>
                  {p.description && <p className="text-[#6B6558] mt-1">{p.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 rounded-full bg-[#E8EFEB] text-[#2B3A33] font-semibold">Rivolta a: {ROLE_LABELS[p.audience_role]}</span>
                    <span className="px-2 py-1 rounded-full bg-[#F1F5F9] text-[#334155]">{total} voti</span>
                    {p.closes_at && <span className="px-2 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] inline-flex items-center gap-1"><Clock className="w-3 h-3" />Chiude {formatDateTime(p.closes_at)}</span>}
                    {p.closed && <span className="px-2 py-1 rounded-full bg-[#FDF2F2] text-[#EF3E36] inline-flex items-center gap-1"><Lock className="w-3 h-3" />Chiusa</span>}
                  </div>
                </div>
                {isAdmin && !p.closed && (
                  <button data-testid={`close-poll-${p.id}`} onClick={() => closePoll(p.id)} className="text-xs px-3 py-1.5 rounded-lg border border-[#E8E2D8] hover:bg-[#FDF2F2]">Chiudi</button>
                )}
              </div>

              <div className="mt-4 space-y-2">
                {p.options.map(opt => {
                  const count = p.results?.[opt.id] || 0;
                  const pct = total ? Math.round((count / total) * 100) : 0;
                  const canVote = !p.user_voted && !p.closed;
                  return (
                    <button
                      key={opt.id}
                      data-testid={`vote-${p.id}-${opt.id}`}
                      onClick={() => canVote && vote(p.id, opt.id)}
                      disabled={!canVote}
                      className={`w-full text-left relative overflow-hidden rounded-xl border transition ${canVote ? "border-[#E8E2D8] hover:border-[#EF3E36] cursor-pointer" : "border-[#E8E2D8] cursor-default"} bg-white`}
                    >
                      <div className="absolute inset-y-0 left-0 bg-[#FDF2F2]" style={{width: `${pct}%`}} />
                      <div className="relative flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2 font-medium text-[#2B3A33]">
                          {p.user_voted && <CheckCircle2 className="w-4 h-4 text-[#EF3E36]" />}
                          {opt.text}
                        </div>
                        <div className="text-sm font-mono text-[#6B6558]">{count} · {pct}%</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {p.user_voted && <div className="mt-3 text-xs text-[#16A34A] font-semibold">✓ Hai già votato in questa votazione</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
